<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useField, useForm } from 'vee-validate'
import { string } from 'yup'
import { VueRecaptcha } from 'vue-recaptcha'

import { useIdeStore } from '@/stores/ide.store'
import { useAuthStore } from '@/stores/auth.store'

import recaptchaService from '@/services/recaptcha.service'
import authService, { type IRegisterRequest } from '@/services/auth.service'
import utilModelsService from '@/services/util.models.service'

import { AUTHMODELHS } from '@/utils/models'
import { RECAPTCHA_KEYS } from '@/utils/recaptcha'

import BaseInput from '@/components/forms/BaseInput.vue'
import Button from '@/components/atoms/button/index.vue'
import SocialLogin from '@/components/header/auth/SocialLoginComp.vue'
import {
  getReferrer,
  getUMT,
  removeReferrer,
  removeUMT
} from '@/services/analytics/analytics.service'

const { errors, handleSubmit, resetForm } = useForm()
const { value: email, errorMessage: emailError } = useField('username', string().required().email())
const { value: password, errorMessage: passwordError } = useField(
  'password',
  string().required().min(5)
)
const { value: confirmPassword, errorMessage: confirmPasswordError } = useField(
  'confirmPassword',
  string().required().min(5)
)

const ideStore = useIdeStore()
const authStore = useAuthStore()
const inputTypePassword = ref<string>('password')
const inputTypeConfirmPassword = ref<string>('password')
const togglePasswordEyeIcon = ref<boolean>(false)
const toggleConfirmPasswordEyeIcon = ref<boolean>(false)
const recaptcha = ref<any>(null)
const httpError = ref<string>('')
const recaptchaToken = ref<string>('')
const recaptchaError = ref<string>('')
const sitekey = ref<string | null>(RECAPTCHA_KEYS.RECAPTCHAREGISTERSITEKEY)
const isLoading = ref<boolean>(false)
const colorTheme = ref<string | null>(ideStore.colorMode)

const iconsToShow = computed(() =>
  togglePasswordEyeIcon.value ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'
)

const iconsToShowConfirm = computed(() =>
  toggleConfirmPasswordEyeIcon.value ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'
)

const passwordNotMatchError = computed(() =>
  !passwordError.value && !confirmPasswordError.value && password.value !== confirmPassword.value
    ? 'Password does not match'
    : null
)

const canSubmit = computed(
  () =>
    Object.keys(errors.value).length === 0 &&
    recaptchaToken.value &&
    !recaptchaError.value &&
    !passwordNotMatchError.value &&
    !isLoading.value
)

/**
 * Handles the response from the reCAPTCHA verification and sets the token
 * @param response - The reCAPTCHA response token
 */
const recaptchaVerified = (response: string) => {
  recaptchaToken.value = response
  recaptchaError.value = ''
}

/**
 * Handles the reCAPTCHA error and sets the error message
 * @param error - The reCAPTCHA error message
 */
const handleRecaptchaError = (error: string) => {
  recaptchaToken.value = ''
  recaptchaError.value = error
  recaptcha.value.reset()
}

/**
 * Toggles the visibility of the password field
 */
const handleShowPassword = () => {
  togglePasswordEyeIcon.value = !togglePasswordEyeIcon.value
  inputTypePassword.value = togglePasswordEyeIcon.value ? 'text' : 'password'
}

/**
 * Toggles the visibility of the confirm password field
 */
const handleShowConfirmPassword = () => {
  toggleConfirmPasswordEyeIcon.value = !toggleConfirmPasswordEyeIcon.value
  inputTypeConfirmPassword.value = toggleConfirmPasswordEyeIcon.value ? 'text' : 'password'
}

/**
 * Handles the form submission
 */
const onSubmit = handleSubmit(async (values) => {
  if (!recaptchaToken.value || recaptchaError.value) {
    handleRecaptchaError("Please click on 'I'm not a robot'")
    return
  }

  const marketing_referrer = getReferrer() || 'null'
  const { marketing_utm_source, marketing_utm_medium, marketing_utm_campaign, marketing_utm_id } =
    getUMT()
  const utm_source = marketing_utm_source || 'null'
  const utm_medium = marketing_utm_medium || 'null'
  const utm_campaign = marketing_utm_campaign || 'null'
  const utm_id = marketing_utm_id || 'null'

  const requestPayload: IRegisterRequest = {
    username: values.username,
    newPassword: values.password,
    retypePassword: values.confirmPassword,
    recaptcha: recaptchaToken.value,
    marketing_referrer:
      marketing_referrer && marketing_referrer !== 'null' ? marketing_referrer : null,
    utm_source: utm_source && utm_source !== 'null' ? utm_source : null,
    utm_medium: utm_medium && utm_medium !== 'null' ? utm_medium : null,
    utm_campaign: utm_campaign && utm_campaign !== 'null' ? utm_campaign : null,
    utm_id: utm_id && utm_id !== 'null' ? utm_id : null
  }

  isLoading.value = true
  await authService
    .register(requestPayload)
    .then((res) => {
      if (res.data.message === 'Registered Successfully') {
        httpError.value = ''
        resetForm()
        authStore.setOtpRedirectFrom('Register')
        authStore.setUserEmailForOTPVerified(values.username)
        utilModelsService.openModal(AUTHMODELHS.OTP)
        removeReferrer()
        removeUMT()
      }
    })
    .catch((error: any) => {
      httpError.value = error?.response?.data?.message || 'An error occurred'
    })
    .finally(() => {
      recaptchaToken.value = ''
      recaptcha.value.reset()
      isLoading.value = false
    })
})

onBeforeMount(async () => {
  sitekey.value = await recaptchaService.getRechapthaKey(RECAPTCHA_KEYS.RECAPTCHAREGISTERSITEKEY)
})
</script>

<template>
  <div>
    <!-- Social Login -->
    <SocialLogin :border="true" />

    <!-- Divider -->
    <div
      class="text-mid before:stroke-secondary-default after:stroke-secondary-default flex items-center p-4 py-4 text-sm before:mr-6 before:flex-[1_1_0%] before:border after:ml-6 after:flex-[1_1_0%] after:border"
    >
      or
    </div>

    <!-- New user register form -->
    <form @submit.prevent="onSubmit">
      <BaseInput
        id="register-email"
        label="Email Address"
        :inputType="'email'"
        placeholder="ilovecoding@jdoodle.com"
        :error="emailError"
        v-model="email"
        :isLightMode="true"
        :isLightGray="false"
        label-class="text-high text-sm font-semibold mt-3"
        :isCustomClass="`border !text-new-secondary-700 dark:!text-white/60  !py-2.5  !bg-new-shade-0 dark:!bg-new-neutral-900 ${
          !emailError && '!border-new-secondary-100 dark:!border-new-neutral-700'
        } `"
      />
      <BaseInput
        id="register_pwd"
        label="Password"
        :inputType="inputTypePassword"
        placeholder="*****"
        :error="passwordError"
        v-model="password"
        :isPassword="true"
        :isPasswordIcon="iconsToShow"
        isPasswordStyling="w-5 h-5 icon-ide-default"
        @click="handleShowPassword"
        :isLightMode="true"
        :isLightGray="false"
        :isEnterKeySupport="true"
        autocomplete="new-password"
        label-class="text-high text-sm font-semibold mt-3"
        :isCustomClass="`border !text-new-secondary-700 dark:!text-white/60  !py-2.5  !bg-new-shade-0 dark:!bg-new-neutral-900 ${
          !passwordError && '!border-new-secondary-100 dark:!border-new-neutral-700'
        } `"
      />
      <BaseInput
        id="register-pass"
        label="Confirm Password"
        :inputType="inputTypeConfirmPassword"
        placeholder="*****"
        :error="confirmPasswordError"
        v-model="confirmPassword"
        :isPassword="true"
        :isPasswordIcon="iconsToShowConfirm"
        isPasswordStyling="w-5 h-5 icon-ide-default"
        @click="handleShowConfirmPassword"
        :isLightMode="true"
        :isLightGray="false"
        :isEnterKeySupport="true"
        autocomplete="new-password"
        label-class="text-high text-sm font-semibold mt-3"
        :isCustomClass="`border  !text-new-secondary-700 dark:!text-white/60  !py-2.5  !bg-new-shade-0 dark:!bg-new-neutral-900 ${
          !confirmPasswordError && '!border-new-secondary-100 dark:!border-new-neutral-700'
        } `"
      />

      <p :class="['p-xsmall mb-3 mt-2', 'error', { hidden: !passwordNotMatchError }]">
        {{ passwordNotMatchError }}
      </p>

      <vue-recaptcha
        ref="recaptcha"
        :sitekey="sitekey || ''"
        :theme="colorTheme === 'dark' ? 'dark' : 'light'"
        @verify="recaptchaVerified"
        @expired="handleRecaptchaError('Recaptcha expired. Please verify again.')"
        @fail="handleRecaptchaError('Recaptcha failed. Please verify again.')"
        @error="recaptchaError"
        class="mt-3"
      />

      <p :class="['p-xsmall mt-1', 'error', { hidden: !recaptchaError }]">
        {{ recaptchaError }}
      </p>

      <Button
        :disabled="!canSubmit"
        color="secondary"
        variant="filled"
        size="medium"
        type="submit"
        class="my-3 w-full rounded-md px-4 py-3"
      >
        Sign Up
      </Button>
    </form>

    <!-- Login prompt -->
    <div class="text-center text-sm font-medium leading-tight">
      <span class="text-mid">Already have an account?</span>
      <Button
        type="button"
        color="secondary"
        variant="link"
        class="text-high ml-1 mt-2 p-0 font-semibold"
        :data-hs-overlay="`#${AUTHMODELHS.LOGIN}`"
      >
        Sign In
      </Button>
    </div>

    <!-- Terms and Condition -->
    <p
      class="text-low leading-norma text-sx mt-4 flex flex-wrap justify-center font-normal sm:text-sm"
    >
      <span class="font-normal leading-normal"> By signing in, you accept our </span>

      <router-link to="/terms" target="_blank" class="text-mid mx-1 font-medium underline">
        Terms & Conditions
      </router-link>
      and
      <router-link to="/privacy" target="_blank" class="text-mid mx-1 font-medium underline">
        Privacy Policy
      </router-link>
    </p>
  </div>
</template>
